import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  styled,
  IconButton,
  TablePagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import axios from 'axios';
import useCustomToast from '../hooks/useCustomToast';
import './AdminPartnerList.css';

const PageContainer = styled(Box)({
  padding: '20px',
  minHeight: '100vh',
  backgroundColor: '#f5f5f5',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: '10px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'flex',
  gap: theme.spacing(2),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '10px',
  '& .MuiTableCell-head': {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: '#f8f8f8',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  '&.contact-cell': {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  }
}));

const ContactInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  '& a': {
    color: '#1976d2',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    }
  }
});

const AdminPartnerList = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { errorToast } = useCustomToast();

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('https://software.tabtrips.com/api/get_partners.php', {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.data.success) {
        setPartners(response.data.partners);
      } else {
        throw new Error(response.data.error || 'Failed to fetch partners');
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      setError('Failed to load partners. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredPartners = partners.filter(partner => 
    Object.values(partner).some(value => 
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  if (loading) {
    return (
      <div className="admin-partner-list">
        <div className="loading">Loading partners...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-partner-list">
        <div className="error">
          {error}
          <button onClick={fetchPartners} className="retry-btn">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-partner-list">
      <h1>Partner Registrations</h1>
      <div className="partner-count">Total Partners: {partners.length}</div>
      
      <div className="partners-table-container">
        <table className="partners-table">
          <thead>
            <tr>
              <th>Hotel Name</th>
              <th>Property Type</th>
              <th>City</th>
              <th>Manager</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Channel Manager</th>
              <th>Submission Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredPartners.map((partner) => (
              <tr key={partner.id}>
                <td>{partner.hotelName}</td>
                <td>{partner.propertyType}</td>
                <td>{partner.cityName}</td>
                <td>{partner.managerName}</td>
                <td>
                  <a href={`mailto:${partner.email}`} className="contact-link email-link">
                    {partner.email}
                  </a>
                </td>
                <td>
                  <a href={`tel:${partner.phone}`} className="contact-link phone-link">
                    {partner.phone}
                  </a>
                </td>
                <td>{partner.channelManagerName || '-'}</td>
                <td>{new Date(partner.submissionDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPartnerList; 